/**
 * PortalDocUpload — public page (no login).
 *
 * Flow:
 *  1. Load token → GET /api/portal/upload-token/{token} → confirms token
 *     shape and prompts for email verify.
 *  2. Candidate types email → POST /verify → returns candidate name + missing
 *     list.
 *  3. Candidate uploads a file per row → POST /upload with email echoed
 *     back for auth on every request.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, ShieldCheck, CheckCircle2, FileText, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalDocUpload() {
    const { token } = useParams();
    const [stage, setStage] = useState("loading"); // loading | verify | ready | done | invalid | expired
    const [email, setEmail] = useState("");
    const [candidate, setCandidate] = useState(null);
    const [missing, setMissing] = useState([]);
    const [uploaded, setUploaded] = useState({});
    const [uploadingKey, setUploadingKey] = useState(null);
    const fileRefs = useRef({});

    useEffect(() => {
        (async () => {
            try {
                await axios.get(`${API}/portal/upload-token/${token}`);
                setStage("verify");
            } catch (err) {
                // Distinguish expired (410) from invalid (404 / anything else)
                if (err?.response?.status === 410) setStage("expired");
                else setStage("invalid");
            }
        })();
    }, [token]);

    const submitVerify = async (e) => {
        e?.preventDefault();
        if (!email.trim()) return;
        try {
            const { data } = await axios.post(`${API}/portal/upload-token/${token}/verify`, { email: email.trim() });
            setCandidate(data.candidate);
            setMissing(data.missing || []);
            setStage(data.missing?.length ? "ready" : "done");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Unable to verify — check the email address on file.");
        }
    };

    const rowKey = (m) => `${m.category}::${m.exp_key}::${m.slot}`;

    const uploadFile = async (m, file) => {
        if (!file) return;
        const k = rowKey(m);
        setUploadingKey(k);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("email", email.trim());
            fd.append("category", m.category);
            fd.append("slot", m.slot);
            fd.append("exp_key", m.category === "experience" ? m.exp_key : "");
            await axios.post(`${API}/portal/upload-token/${token}/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUploaded((s) => ({ ...s, [k]: file.name }));
            toast.success(`${m.slot_label} uploaded`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingKey(null);
        }
    };

    if (stage === "loading") {
        return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Loading…</div>;
    }
    if (stage === "invalid" || stage === "expired") {
        const isExpired = stage === "expired";
        return (
            <div className="min-h-screen grid place-items-center p-6" data-testid={isExpired ? "portal-upload-expired" : "portal-upload-invalid"}>
                <div className="max-w-md text-center border border-border rounded-lg p-6">
                    <div className="text-4xl mb-2">{isExpired ? "⏳" : "🔒"}</div>
                    <div className="font-bold text-xl">
                        {isExpired ? "This upload link has expired." : "This upload link is invalid."}
                    </div>
                    <div className="text-sm text-muted-foreground mt-2">
                        Please ask the recruiter to send you a fresh link.
                    </div>
                </div>
            </div>
        );
    }
    if (stage === "done") {
        return (
            <div className="min-h-screen grid place-items-center p-6" data-testid="portal-upload-done">
                <div className="max-w-md text-center border border-emerald-200 bg-emerald-50 rounded-lg p-6">
                    <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
                    <div className="font-bold text-xl mt-3">All docs are on file 🎉</div>
                    <div className="text-sm text-muted-foreground mt-2">
                        Nothing to upload right now. You can close this window.
                    </div>
                </div>
            </div>
        );
    }
    if (stage === "verify") {
        return (
            <div className="min-h-screen grid place-items-center p-6">
                <form
                    onSubmit={submitVerify}
                    className="max-w-md w-full border border-border rounded-lg p-6 space-y-4"
                    data-testid="portal-upload-verify"
                >
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="h-6 w-6 text-ikb" />
                        <div className="font-bold text-xl">Verify your identity</div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Please confirm the email address the recruiter has on file, then you&apos;ll see the docs to upload.
                    </div>
                    <div>
                        <label className="text-xs font-medium mb-1 block">Email address</label>
                        <Input
                            type="email"
                            data-testid="portal-upload-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.com"
                            required
                        />
                    </div>
                    <Button
                        type="submit"
                        className="w-full"
                        data-testid="portal-upload-verify-submit"
                    >
                        <MailCheck className="h-4 w-4 mr-2" /> Continue
                    </Button>
                </form>
            </div>
        );
    }
    return (
        <div className="min-h-screen p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="portal-upload-ready">
            <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Document upload</div>
                <div className="text-2xl font-bold">Hi {candidate?.name?.split(" ")[0] || "there"} 👋</div>
                <div className="text-sm text-muted-foreground mt-1">
                    Please upload the following {missing.length} document{missing.length === 1 ? "" : "s"} so the team can move your application forward.
                </div>
            </div>
            <div className="space-y-2">
                {missing.map((m) => {
                    const k = rowKey(m);
                    const done = uploaded[k];
                    return (
                        <div
                            key={k}
                            data-testid={`portal-upload-row-${m.category}-${m.slot}`}
                            className={`border rounded p-3 flex items-center justify-between gap-3 ${done ? "border-emerald-200 bg-emerald-50" : "border-border"}`}
                        >
                            <div className="min-w-0">
                                <div className="text-sm font-semibold flex items-center gap-2">
                                    <FileText className={`h-4 w-4 ${done ? "text-emerald-700" : "text-muted-foreground"}`} />
                                    {m.slot_label}
                                </div>
                                {m.category === "experience" && (
                                    <div className="text-xs text-muted-foreground mt-0.5">{m.exp_label}</div>
                                )}
                                {done && (
                                    <div className="text-xs text-emerald-700 mt-0.5">
                                        Uploaded: {done}
                                    </div>
                                )}
                            </div>
                            <div>
                                {done ? (
                                    <span className="text-emerald-700 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest">
                                        <CheckCircle2 className="h-4 w-4" /> Done
                                    </span>
                                ) : (
                                    <>
                                        <Button
                                            size="sm"
                                            onClick={() => fileRefs.current[k]?.click()}
                                            disabled={uploadingKey === k}
                                            data-testid={`portal-upload-btn-${m.category}-${m.slot}`}
                                        >
                                            <Upload className="h-4 w-4 mr-1" />
                                            {uploadingKey === k ? "Uploading…" : "Upload"}
                                        </Button>
                                        <input
                                            type="file"
                                            className="hidden"
                                            ref={(el) => { fileRefs.current[k] = el; }}
                                            onChange={(e) => uploadFile(m, e.target.files?.[0])}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="text-xs text-muted-foreground text-center pt-4">
                Trouble uploading? Reply to the recruiter&apos;s email — they can help.
            </div>
        </div>
    );
}
